import React, { useState } from "react";
import { IMessage, AssignmentStatusEnum, SectionTypeEnum } from "@imaginelearning/mps-api-service";
import {
  UxAction,
  UxAvatar,
  UxAvatarEnum,
  UxAvatarPriorityEnum,
  UxIcon,
  UxIconEnum,
  UxModal,
  UxPositionEnum,
  UxSizeEnum,
  UxSizeExtendedEnum,
  UxStyleEnum
} from "@imaginelearning/mps-ux-component-library";
import Confetti from "assets/confetti";
import { useTranslation } from "react-i18next";
import "./activity-transition-modal.scss";
import useGradeRange from "hooks/usegraderange";
import HorizontalRule from "./horizontal-rule";
import AudioButton from "./audiobutton";
import useTheme from "../../../hooks/usetheme";

interface IActivityTransitionModalProps {
  showModal?: boolean;
  isLastMasteryCheck: boolean;
  starsEarned?: number;
  message: Element | string | IMessage[]; // this complexity may lead to bugs
  activityStatus: AssignmentStatusEnum;
  activityType: SectionTypeEnum;
  handleModalClose: (close?: boolean) => void;
  handleModalExit?: Function;
  avatar?: UxAvatarEnum;
  totalScore?: number | null;
  takeTotal?: number | null;
  displayScore?: boolean;
  scoreOverride?: number;
  enableAudio?: boolean;
  showResultIcon?: boolean;
  ActionButtonText?: string;
}

const MODAL_DIALOG_ID = "mpng-ux-modal-activity-transition";

function ActivityTransitionModal({
  showModal = true,
  isLastMasteryCheck,
  starsEarned = 0,
  message,
  activityStatus,
  activityType,
  handleModalClose,
  handleModalExit,
  avatar,
  totalScore,
  takeTotal,
  displayScore = true,
  enableAudio = true,
  showResultIcon = true,
  scoreOverride,
  ActionButtonText = "Go"
}: IActivityTransitionModalProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isKto2, is3to5, isKto8, is9to12 } = useGradeRange();
  const [showLoading, setShowLoading] = useState(false);

  const hasAvatar = isKto8 && avatar;
  const withAvatarClassName = hasAvatar ? "with-avatar" : "";
  const statusPassed = activityStatus === AssignmentStatusEnum.PASSED;
  const statusNotPassed = activityStatus === AssignmentStatusEnum.NOT_PASSED;
  const masteryCheckComplete = statusNotPassed || activityStatus === "Passed";
  const actualStarsEarned = starsEarned ?? 0;

  const modalClose = () => {
    setShowLoading(true);
    handleModalClose(true);
  };
  const modalGo = () => {
    setShowLoading(true);
    handleModalClose();
  };
  const modalExit = () => {
    setShowLoading(true);
    if (handleModalExit) {
      handleModalExit();
    }
  };

  // TODO Refactor this!!! size should be relative to the available space in the viewport
  const getModalSize = (): UxSizeEnum => {
    if (activityType === "mastery-check") {
      // failing on any other than the last mastery check
      if (activityStatus === AssignmentStatusEnum.NOT_PASSED && !isLastMasteryCheck) {
        return UxSizeEnum.MEDIUM;
      }
    }
    return UxSizeEnum.LARGE;
  };

  let UXModalClassName = "";
  if (activityStatus === AssignmentStatusEnum.NOT_STARTED) {
    UXModalClassName = "activity-transition-modal-notstarted";
  } else if (activityStatus === AssignmentStatusEnum.IN_PROGRESS) {
    // ??? This was missing....
  } else if (activityStatus === AssignmentStatusEnum.PASSED) {
    UXModalClassName = "activity-transition-modal-passed";
  } else if (activityStatus === AssignmentStatusEnum.NOT_PASSED) {
    UXModalClassName = "activity-transition-modal-notpassed";
  }

  const backdropOpacity = statusPassed || statusNotPassed ? "backdrop-transparent" : "backdrop-opaque";

  // This could still use some work
  let messageHeader = "";
  let messageBody = "";
  if (message && typeof message === "string") {
    messageBody = message;
  } else if (Array.isArray(message)) {
    message.forEach((message: any) => {
      if (message.type === 0) {
        messageHeader = t(message.translationKey, message.message);
      } else if (message.type === 1) {
        messageBody = t(message.translationKey, message.message);
      }
    });
  }

  const scorePercent = (scoreOverride?: number) => {
    if (scoreOverride) return scoreOverride;
    if (totalScore === 0 || takeTotal === 0) return 0;
    if (totalScore && takeTotal) {
      const score = (totalScore / takeTotal) * 100;
      return Math.round(score).toString();
    }
    return null;
  };

  /**
   * Only display the score info if the displayScore prop is true
   * and the activityType is not ACTIVITY
   * @returns boolean
   */
  const displayScoreInfo = (): boolean => {
    if (!displayScore || activityType === SectionTypeEnum.ACTIVITY) return false;
    return true;
  };

  return (
    <UxModal
      id={MODAL_DIALOG_ID}
      className={`activity-transition-modal ${UXModalClassName} ${theme}`}
      backdropClassName={backdropOpacity}
      size={getModalSize()}
      showModal={showModal}
      showClose={false}
      handleClose={modalClose}
      backdrop="static"
      hideBorders={true}
      contentClassName={`activity-transition-modal-content ${withAvatarClassName}`}
      title={[
        <>
          <UxAction
            key={2}
            style={is9to12 ? UxStyleEnum.BOXED : UxStyleEnum.CIRCLE}
            className="modal-close-button"
            icon={{ icon: UxIconEnum.TIMES }}
            callback={() => handleModalClose(true)}
          />
          {enableAudio && <AudioButton key={1} container={MODAL_DIALOG_ID} />}
          {masteryCheckComplete && showResultIcon && (
            <div key={3} className="celebration-wrapper">
              <UxIcon
                icon={statusNotPassed ? UxIconEnum.RED_CIRCLE_XMARK : UxIconEnum.GREEN_CIRCLE_CHECK}
                size={is9to12 ? UxSizeExtendedEnum.X1 : UxSizeExtendedEnum.X2}
                className="status-icon"
              />
            </div>
          )}
          <div key={4} className="message-header">
            {messageHeader && <h1>{messageHeader}</h1>}
          </div>
          {isKto8 && statusPassed ? <Confetti key={5} className="confetti" /> : null}
        </>
      ]}
      body={
        <div>
          {messageBody && (
            <div key={10} className="message-text text-center">
              {messageBody}
            </div>
          )}
          {(actualStarsEarned > 0 || (displayScore && (takeTotal ?? 0) > 0)) && messageBody && (
            <HorizontalRule key={11} />
          )}
          {displayScoreInfo() && (takeTotal ?? 0) > 0 && (
            <>
              <div className="text-center" id="activity-transition-modal-score-section">
                <span className="point-line">{t("You got")}</span> <span className="point-value">{totalScore}</span>{" "}
                <span className="point-line">{t("out of")}</span> <span className="point-value">{takeTotal}</span>{" "}
                <span className="point-line">{t("answers correct")}</span>.
              </div>
              <KeyValue label="Your score" styleName="score-percentage">
                {scorePercent(scoreOverride)}%
              </KeyValue>
            </>
          )}
          {isKto8 && actualStarsEarned > 0 && (
            <KeyValue key={12} label="You earned" styleName="score-stars">
              <UxIcon
                icon={UxIconEnum.STAR}
                size={UxSizeExtendedEnum.X1}
                className="pb-2"
                alt={actualStarsEarned > 1 ? t("Stars") : t("Star")}
              />
              {actualStarsEarned}
            </KeyValue>
          )}
        </div>
      }
      footer={[
        <>
          <UxAction
            key={21}
            className="btn-primary go-button px-4 mx-2"
            id="go-button"
            enabled={!showLoading}
            text={t(ActionButtonText)}
            textClassName="h2"
            style={UxStyleEnum.SQUARED}
            callback={modalGo}
            icon={{
              textPosition: UxPositionEnum.LEFT,
              icon: isKto8 ? UxIconEnum.ARROW_RIGHT : undefined,
              size: UxSizeEnum.LARGE
            }}
          />
          {handleModalExit && (
            <UxAction
              key={22}
              className="btn-secondary exit-button px-4 mx-2 "
              id="exit-button"
              enabled={!showLoading}
              text={t("Exit")}
              textClassName="h2"
              style={UxStyleEnum.ROUNDED}
              callback={modalExit}
              icon={{
                textPosition: UxPositionEnum.LEFT,
                icon: UxIconEnum.TIMES,
                size: UxSizeEnum.LARGE
              }}
            />
          )}
          {hasAvatar && (
            <UxAvatar
              key={31}
              className="activity-transition-avatar no-audio"
              avatar={avatar}
              avatarType={
                (statusPassed && isKto2) || (statusPassed && is3to5)
                  ? UxAvatarPriorityEnum.CELEBRATE
                  : UxAvatarPriorityEnum.DEFAULT
              }
              size={UxSizeExtendedEnum.X5}
              theme={theme}
            />
          )}
        </>
      ]}
    />
  );
}

function KeyValue({ label = "", styleName = "", children }: any) {
  const { t } = useTranslation();
  return (
    <div className="message-subtext text-center">
      <span>{t(label)}: </span>
      <span className={`score-value ${styleName}`}>{children}</span>
    </div>
  );
}

export default ActivityTransitionModal;
